/**
 * RAM — shared types.
 *
 * Per modules digest §RAM + procedures digest §RAM.
 */

export type RamActionCode =
  | 'address-change'
  | 'hold-or-stop'
  | 'back-on-track'
  | 'dormant-release'
  | 'signature-card'
  | 'poa-suspend-revoke'
  | 'termination-of-agent'
  | 'utma-successor'
  // ── Wave 10 — RAM completeness ─────────────────────────────────────
  | 'change-title'
  | 'change-ownership'
  | 'change-od-protection'
  | 'change-od-limit'
  | 'change-statement-prefs'
  | 'change-features'
  | 'block-unblock'
  | 'close-account'
  | 'reactivate-dormant'
  | 'change-rate-index';

export interface RamActionDefinition {
  code: RamActionCode;
  title: string;
  description: string;
  /** True if a final definition from Collette is still outstanding. */
  pendingClarification?: boolean;
}

export const RAM_ACTIONS: ReadonlyArray<RamActionDefinition> = [
  {
    code: 'address-change',
    title: 'Address change',
    description: 'Update a customer\'s mailing / residence address via CustMod.',
  },
  {
    code: 'hold-or-stop',
    title: 'Hold or stop payment',
    description: 'Place a hold or stop-pay on an account via AcctMod.',
  },
  {
    code: 'back-on-track',
    title: 'Back on Track',
    description: 'Customer-assistance re-enrollment (pending Collette clarification).',
    pendingClarification: true,
  },
  {
    code: 'dormant-release',
    title: 'Dormant release',
    description: 'Release dormant status on an eligible account.',
  },
  {
    code: 'signature-card',
    title: 'Signature card update',
    description: 'Send a signature-card update for e-sign via SignNow.',
  },
  {
    code: 'poa-suspend-revoke',
    title: 'POA / suspend / revoke',
    description: 'Record POA, suspend, or revoke actions on a CIF/account.',
  },
  {
    code: 'termination-of-agent',
    title: 'Termination of agent',
    description: 'Terminate an agency/authorized-signer relationship.',
  },
  {
    code: 'utma-successor',
    title: 'UTMA successor',
    description: 'Designate a UTMA successor custodian (pending Collette clarification).',
    pendingClarification: true,
  },
  // ── Wave 10 — RAM completeness (A5 BLOCKER) ────────────────────────
  {
    code: 'change-title',
    title: 'Change account title',
    description: 'Retitle an account (signature card refresh required).',
  },
  {
    code: 'change-ownership',
    title: 'Change ownership type',
    description: 'Change CallClass / ownership type — Compliance review.',
  },
  {
    code: 'change-od-protection',
    title: 'Change OD protection',
    description: 'Toggle overdraft protection settings on an account.',
  },
  {
    code: 'change-od-limit',
    title: 'Change OD limit',
    description: 'Increase / decrease overdraft limit (Supervisor approval over $500).',
  },
  {
    code: 'change-statement-prefs',
    title: 'Change statement preferences',
    description: 'Toggle e-statement / paper / frequency (closes NEWESI loop).',
  },
  {
    code: 'change-features',
    title: 'Add / remove account features',
    description: 'Per-feature flags on an account.',
  },
  {
    code: 'block-unblock',
    title: 'Block / unblock account',
    description: 'Place or remove a block on the account (Manager review).',
  },
  {
    code: 'close-account',
    title: 'Close account',
    description: 'Graceful close (Manager) or force close (Compliance + Manager).',
  },
  {
    code: 'reactivate-dormant',
    title: 'Reactivate dormant',
    description: 'Reactivate a dormant account — Supervisor approval.',
  },
  {
    code: 'change-rate-index',
    title: 'Change rate index (CD)',
    description: 'Change the rate-index code on a CD.',
  },
];

export interface RamTarget {
  /** Selected CIF id (7-char zero-padded). */
  cifId: string;
  /** Customer display name (preserved for header display). */
  customerName?: string;
  /** Optional account id + type when action targets a specific account. */
  acctId?: string;
  acctType?: string;
}
